import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, LayoutGrid, Search, X } from 'lucide-react';
import { resolveApiAssetUrl } from '../lib/api';
import { normalizeProviderName } from '../lib/providerLogos';

/**
 * Logolu sağlayıcı seçicisi — katalog araç çubuğundaki yerel `<select>`'in
 * yerine geçer (bugün yalnızca Betrun şemasında, bkz. GameCatalog).
 *
 * Yerel select'in açılır listesi işletim sisteminin çizdiği düz bir yazı
 * listesidir: logo taşıyamaz ve yüz küsur sağlayıcıda oyuncu markayı adından
 * aramak zorunda kalıyordu. Burada her satır logoyu ve adı birlikte çizer,
 * listenin başında bir arama kutusu durur.
 *
 * Logo `providersFromResponse` ile çözülmüş gelir (panelden yüklenen logo →
 * gömülü varsayılan); logosu olmayan sağlayıcıda baş harf rozeti çizilir —
 * satırların hizası logolu satırlarla aynı kalsın diye.
 */
const ProviderLogo = ({ provider }) => {
  const [broken, setBroken] = useState(false);
  if (provider.logo && !broken) {
    return (
      <span className="provider-picker-logo">
        <img
          src={resolveApiAssetUrl(provider.logo)}
          alt=""
          loading="lazy"
          onError={() => setBroken(true)}
        />
      </span>
    );
  }
  return (
    <span className="provider-picker-logo is-initial" aria-hidden="true">
      {String(provider.name || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
};

const ProviderPicker = ({ providers, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const selected = providers.find((item) => item.slug === value) || null;
  const term = normalizeProviderName(query);
  const visible = useMemo(() => {
    if (!term) return providers;
    return providers.filter((item) => (
      normalizeProviderName(item.name).includes(term)
      || normalizeProviderName(item.slug).includes(term)
    ));
  }, [providers, term]);

  // Dışarı tıklama ve Esc listeyi kapatır; liste açıkken arama kutusu
  // odaklanır ki oyuncu yazmaya hemen başlayabilsin (masaüstünde).
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer, { passive: true });
    document.addEventListener('keydown', onKey);
    if (window.matchMedia?.('(hover: hover)').matches) searchRef.current?.focus();
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => { if (!open) setQuery(''); }, [open]);

  const choose = (slug) => {
    onChange(slug);
    setOpen(false);
  };

  return (
    <div className="provider-picker" ref={rootRef}>
      <button
        type="button"
        className="pusu-input provider-picker-trigger"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Sağlayıcı filtrele"
      >
        {selected ? (
          <>
            <ProviderLogo provider={selected} />
            <span className="provider-picker-label">{selected.name}</span>
          </>
        ) : (
          <>
            <span className="provider-picker-logo is-all"><LayoutGrid size={14} /></span>
            <span className="provider-picker-label">Tüm Sağlayıcılar</span>
          </>
        )}
        <ChevronDown size={16} className={`provider-picker-caret ${open ? 'is-open' : ''}`} />
      </button>

      {open && (
        <div className="provider-picker-panel">
          <div className="provider-picker-search">
            <Search size={14} />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Sağlayıcı ara (${providers.length})`}
              aria-label="Sağlayıcı ara"
              autoComplete="off"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} aria-label="Aramayı temizle">
                <X size={14} />
              </button>
            )}
          </div>
          <ul className="provider-picker-list" role="listbox" aria-label="Sağlayıcılar">
            {!term && (
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={!selected}
                  className={`provider-picker-option ${!selected ? 'is-active' : ''}`}
                  onClick={() => choose('all')}
                >
                  <span className="provider-picker-logo is-all"><LayoutGrid size={14} /></span>
                  <span className="provider-picker-label">Tüm Sağlayıcılar</span>
                  {!selected && <Check size={15} className="provider-picker-check" />}
                </button>
              </li>
            )}
            {visible.map((item) => {
              const active = item.slug === value;
              return (
                <li key={item.slug}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`provider-picker-option ${active ? 'is-active' : ''}`}
                    onClick={() => choose(item.slug)}
                    title={item.name}
                  >
                    <ProviderLogo provider={item} />
                    <span className="provider-picker-label">{item.name}</span>
                    {active && <Check size={15} className="provider-picker-check" />}
                  </button>
                </li>
              );
            })}
            {term && visible.length === 0 && (
              <li className="provider-picker-empty">
                “{query.trim()}” ile eşleşen sağlayıcı bulunamadı.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProviderPicker;
